import { useState } from 'react';
import axios from 'axios';
import { useNavigate } from '@tanstack/react-router';
import { ArrowLeft, CreditCard, QrCode, FileText, Loader2, Check, AlertCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import { useTopUp, type TopUpResponse } from '@/hooks/useBalance';
import { Card, CardContent } from '@mustauto/ui/web';
import { useToast } from '@mustauto/ui/web';
import { formatReais } from '@/lib/utils';

const PRESET_AMOUNTS = [500, 1000, 2500, 5000, 10000, 20000];

// Espelha a validação do servidor: min(100) / max(50000). Mesmas mensagens da API (Zod pt-BR).
const MIN_AMOUNT = 100;
const MAX_AMOUNT = 50000;
const MIN_AMOUNT_MESSAGE = 'Valor mínimo de R$100';
const MAX_AMOUNT_MESSAGE = 'Valor máximo por depósito é R$50.000';

const PAYMENT_METHODS = [
  { value: 'pix' as const, label: 'PIX', icon: QrCode, description: 'Confirmação instantânea' },
  { value: 'credit_card' as const, label: 'Cartão de crédito', icon: CreditCard, description: 'Confirmação instantânea' },
  { value: 'boleto' as const, label: 'Boleto bancário', icon: FileText, description: '1-3 dias úteis' },
];

const PAYMENT_METHOD_LABELS: Record<'pix' | 'boleto' | 'credit_card', string> = {
  pix: 'PIX',
  boleto: 'boleto',
  credit_card: 'cartão de crédito',
};

/** Extrai a mensagem da API (Zod pt-BR) quando houver; senão, fallback genérico. */
function extractErrorMessage(err: unknown): string {
  if (axios.isAxiosError(err)) {
    const apiMessage = (err.response?.data as { message?: string } | undefined)?.message;
    if (apiMessage) return apiMessage;
  }
  return 'Não foi possível iniciar o depósito. Tente novamente.';
}

export function TopUpPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [amount, setAmount] = useState(5000);
  const [customAmount, setCustomAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState<'pix' | 'boleto' | 'credit_card'>('pix');
  const [result, setResult] = useState<TopUpResponse | null>(null);
  const [reviewing, setReviewing] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const topUpMutation = useTopUp();

  const effectiveAmount = customAmount ? Number(customAmount) : amount;
  const customAmountNumber = customAmount ? Number(customAmount) : null;
  const isFinite = Number.isFinite(effectiveAmount);
  const belowMin = !isFinite || effectiveAmount < MIN_AMOUNT;
  const aboveMax = isFinite && effectiveAmount > MAX_AMOUNT;
  const isValidAmount = isFinite && !belowMin && !aboveMax;

  // Mensagem de validação inline do campo (espelha o servidor).
  const amountFieldError =
    customAmountNumber !== null && customAmountNumber < MIN_AMOUNT
      ? MIN_AMOUNT_MESSAGE
      : customAmountNumber !== null && customAmountNumber > MAX_AMOUNT
        ? MAX_AMOUNT_MESSAGE
        : null;

  // Passo 1: validar e ir para a revisão (não cobra ainda).
  function handleReview() {
    if (!isValidAmount) return;
    setSubmitError(null);
    setReviewing(true);
  }

  // Passo 2: confirmar de fato — dispara o mutate com try/catch.
  async function handleConfirm() {
    if (!isValidAmount) return;
    setSubmitError(null);
    try {
      const res = await topUpMutation.mutateAsync({ amount: effectiveAmount, paymentMethod });
      setResult(res);
    } catch (err) {
      const message = extractErrorMessage(err);
      setSubmitError(message);
      toast({ type: 'error', title: 'Falha no depósito', description: message });
    }
  }

  // After payment created — show confirmation
  if (result) {
    return (
      <motion.div
        className="mx-auto max-w-lg space-y-6"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      >
        <button
          onClick={() => navigate({ to: '/balance' })}
          className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft size={14} />
          Voltar ao saldo
        </button>

        <Card>
          <CardContent className="space-y-4 pt-6 text-center">
            {paymentMethod === 'pix' && result.pixQrCode && (
              <>
                <h2 className="text-lg font-bold">Escaneie o QR Code</h2>
                <p className="text-sm text-muted-foreground">
                  Abra o app do seu banco e escaneie o código PIX abaixo
                </p>
                <div className="mx-auto flex h-48 w-48 items-center justify-center rounded-lg border border-border bg-white p-4">
                  <img
                    src={`data:image/png;base64,${result.pixQrCode}`}
                    alt="QR Code PIX"
                    className="h-full w-full"
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  Valor: {formatReais(effectiveAmount)}
                  {result.pixExpiresAt && (
                    <> — Expira em {new Date(result.pixExpiresAt).toLocaleString('pt-BR')}</>
                  )}
                </p>
              </>
            )}

            {paymentMethod === 'boleto' && (
              <>
                <h2 className="text-lg font-bold">Boleto gerado</h2>
                <p className="text-sm text-muted-foreground">
                  Pague o boleto de {formatReais(effectiveAmount)}. O saldo será creditado em até 3 dias úteis.
                </p>
                {result.boletoUrl && (
                  <a
                    href={result.boletoUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 rounded-lg bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90"
                  >
                    <FileText size={14} />
                    Abrir boleto
                  </a>
                )}
                {result.boletoBarcode && (
                  <p className="mt-2 font-mono text-xs text-muted-foreground break-all">
                    {result.boletoBarcode}
                  </p>
                )}
              </>
            )}

            {paymentMethod === 'credit_card' && result.stripeClientSecret && (
              <>
                <div className="flex h-16 w-16 mx-auto items-center justify-center rounded-full bg-green-100 text-green-600 dark:bg-green-950/40 dark:text-green-400">
                  <Check size={28} />
                </div>
                <h2 className="text-lg font-bold">Pagamento iniciado</h2>
                <p className="text-sm text-muted-foreground">
                  Confirme o pagamento de {formatReais(effectiveAmount)} no formulário do Stripe.
                </p>
              </>
            )}

            <button
              onClick={() => navigate({ to: '/balance' })}
              className="mt-4 w-full rounded-lg border border-border py-2.5 text-sm font-medium hover:bg-muted"
            >
              Voltar ao saldo
            </button>
          </CardContent>
        </Card>
      </motion.div>
    );
  }

  // Review step — confirm before charging real money.
  if (reviewing) {
    return (
      <motion.div
        className="mx-auto max-w-lg space-y-6"
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <button
          onClick={() => { setReviewing(false); setSubmitError(null); }}
          className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft size={14} />
          Voltar
        </button>

        <div>
          <h1 className="text-2xl font-bold">Confirmar depósito</h1>
          <p className="text-sm text-muted-foreground">Revise os dados antes de continuar</p>
        </div>

        <Card>
          <CardContent className="space-y-4 pt-6">
            <p className="text-base">
              Você está depositando{' '}
              <strong className="font-bold">{formatReais(effectiveAmount)}</strong>{' '}
              via {PAYMENT_METHOD_LABELS[paymentMethod]}.
            </p>

            {submitError && (
              <div className="flex items-start gap-2 rounded-lg border border-destructive/40 bg-destructive/10 p-3">
                <AlertCircle size={16} className="mt-0.5 shrink-0 text-destructive" />
                <p className="text-sm text-destructive">{submitError}</p>
              </div>
            )}

            <div className="flex gap-2">
              <button
                onClick={() => { setReviewing(false); setSubmitError(null); }}
                disabled={topUpMutation.isPending}
                className="flex-1 rounded-lg border border-border py-2.5 text-sm font-medium hover:bg-muted disabled:opacity-50"
              >
                Voltar
              </button>
              <button
                onClick={handleConfirm}
                disabled={topUpMutation.isPending}
                className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-primary py-2.5 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-50"
              >
                {topUpMutation.isPending ? <Loader2 size={16} className="animate-spin" /> : null}
                Confirmar
              </button>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    );
  }

  return (
    <motion.div
      className="mx-auto max-w-2xl space-y-6"
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <button
        onClick={() => navigate({ to: '/balance' })}
        className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft size={14} />
        Voltar ao saldo
      </button>

      <div>
        <h1 className="text-2xl font-bold">Depositar</h1>
        <p className="text-sm text-muted-foreground">Escolha o valor e o método de pagamento</p>
      </div>

      {/* Amount selection */}
      <Card>
        <CardContent className="space-y-4 pt-6">
          <h3 className="text-sm font-semibold">Valor do depósito</h3>
          <div className="grid grid-cols-3 gap-2">
            {PRESET_AMOUNTS.map((preset) => (
              <button
                key={preset}
                onClick={() => { setAmount(preset); setCustomAmount(''); }}
                className={`rounded-lg border p-3 text-sm font-semibold transition-colors ${
                  !customAmount && amount === preset
                    ? 'border-primary bg-primary/5 text-primary'
                    : 'border-border hover:bg-muted'
                }`}
              >
                {formatReais(preset)}
              </button>
            ))}
          </div>
          <div>
            <label className="mb-1 block text-xs text-muted-foreground">Ou digite um valor personalizado</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">R$</span>
              <input
                type="number"
                value={customAmount}
                onChange={(e) => setCustomAmount(e.target.value)}
                placeholder="0,00"
                min={MIN_AMOUNT}
                max={MAX_AMOUNT}
                className="w-full rounded-lg border border-border bg-background py-2.5 pl-10 pr-4 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>
            {amountFieldError && (
              <p className="mt-1 text-xs text-destructive">{amountFieldError}</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Payment method */}
      <Card>
        <CardContent className="space-y-4 pt-6">
          <h3 className="text-sm font-semibold">Método de pagamento</h3>
          <div className="space-y-2">
            {PAYMENT_METHODS.map((pm) => {
              const Icon = pm.icon;
              return (
                <label
                  key={pm.value}
                  className={`flex cursor-pointer items-center gap-3 rounded-lg border p-4 transition-colors ${
                    paymentMethod === pm.value
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:bg-muted'
                  }`}
                >
                  <input
                    type="radio"
                    name="paymentMethod"
                    value={pm.value}
                    checked={paymentMethod === pm.value}
                    onChange={() => setPaymentMethod(pm.value)}
                    className="h-4 w-4 accent-primary"
                  />
                  <Icon size={20} className="text-muted-foreground" />
                  <div>
                    <div className="text-sm font-medium">{pm.label}</div>
                    <div className="text-xs text-muted-foreground">{pm.description}</div>
                  </div>
                </label>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Continue to review */}
      <button
        onClick={handleReview}
        disabled={!isValidAmount}
        className="flex w-full items-center justify-center gap-2 rounded-lg bg-primary py-3 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-50"
      >
        Revisar {isValidAmount ? formatReais(effectiveAmount) : ''}
      </button>
    </motion.div>
  );
}
